import React from "react";
import { useNavigate } from "react-router-dom";
import { TaskListItem as TaskListItemType } from "@myapp/contract";
import { formatDateDDMMYYYY } from "../../../../shared/helpers/date";
import { formatSollIst } from "../../../../shared/helpers/duration";
import { routes } from "../../../../shared/config/routes";
import { ListItem } from "../../../../shared/ui/list/ListIteam/ListItem";
import { aufgabenColumns } from "../model/constants";
import {
  getTaskTypeLabel,
  getTaskStatusLabel,
  getTaskStatusColor,
  canPlanTask,
  canReplanTask,
  canDeleteTask,
  useDeleteTask,
  NEEDS_RESCHEDULING_LABEL,
  NEEDS_RESCHEDULING_COLOR,
} from "../../../../entities/task";
import { useUpsertTask } from "../../../../features/task-upsert";
import { IconButton } from "../../../../shared/ui/icons/IconButton/IconButton";
import DeleteItemDialog from "../../../../shared/ui/feedback/DeleteItemDialog/DeleteItemDialog";
import { useDeleteDialog } from "../../../../shared/hooks/useDeleteDialog";
import { CalendarClock, Trash2 } from "lucide-react";

interface Props {
  item: TaskListItemType;
}

function formatScheduledDateRange(plannedStart: string, plannedWorkDurationMinutes: number): string {
  const start = new Date(plannedStart);
  const end = new Date(start.getTime() + plannedWorkDurationMinutes * 60 * 1000);

  const datePart = formatDateDDMMYYYY(plannedStart);
  const startTime = `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`;
  const endTime = `${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`;

  return `${datePart} ${startTime} - ${endTime}`;
}


export const AufgabenListItem: React.FC<Props> = ({ item }) => {
  const { openEdit } = useUpsertTask();
  const navigate = useNavigate();
  const deleteDialog = useDeleteDialog();
  const deleteTask = useDeleteTask();

  const datum = item.schedule?.plannedWorkStartsAt
    ? formatScheduledDateRange(item.schedule?.plannedWorkStartsAt, item.schedule?.plannedWorkMinutes)
    : "—";
  const abgabefrist = item.deadline ? formatDateDDMMYYYY(item.deadline) : "—";
  const soll = formatSollIst(item.schedule?.plannedTravelMinutes, item.schedule?.plannedWorkMinutes, item.schedule?.plannedDepartureMinutes);
  const ist = formatSollIst(item.schedule?.actualTravelMinutes, item.schedule?.actualWorkMinutes, item.schedule?.actualDepartureMinutes);

  /** Non-null exactly when the row offers "Umplanen" — and it is what the route needs. */
  const replanFrom = canReplanTask(item) ? item.schedule?.plannedWorkStartsAt ?? null : null;

  // Absent for work that is done or already begun — the API refuses those with 409, so the button
  // would only ever produce an error.
  const canDelete = canDeleteTask(item);

  /**
   * Label plus dot, the same pair the Auftrag list shows. Umdisponierung outranks the status
   * while it is flagged, and it replaces the colour along with the text — a row that reads
   * "Braucht Umdisponierung" in the blue of "Geplant" says two different things at once.
   */
  const needsRescheduling = item.schedule?.needsRescheduling ?? false;
  const statusLabel = needsRescheduling
    ? NEEDS_RESCHEDULING_LABEL
    : getTaskStatusLabel(item.status);
  const statusColor = needsRescheduling
    ? NEEDS_RESCHEDULING_COLOR
    : getTaskStatusColor(item.status);

  const askDelete = () =>
    deleteDialog.openDelete(
      "Aufgabe wirklich löschen?",
      async ({ forFullSeries }) => {
        await deleteTask.mutateAsync({ id: item.id, forFullSeries });
      },
      // An occurrence and the rest of its rule are different deletions — the dialog asks which.
      {
        isSeries: item.seriesId != null,
        description: item.seriesId != null ? "Diese Aufgabe gehört zu einer Serie." : undefined,
      },
    );

  return (
    <>
      <div
        onClick={() => openEdit(item.id)}
      >
        <ListItem
          columns={aufgabenColumns}
          actionsOnHover
          // Always passed, even when empty: the actions track has to exist on every row, or rows
          // without the button would lay their cells out differently from their neighbours.
          actions={
            <>
              {replanFrom && (
                <IconButton
                className="min-w-[40px]"
                  variant="primary"
                  icon={<CalendarClock size={16} />}
                  label="Umplanen"
                  aria-label="Auf Kalender verschieben"
                  title="Auf Kalender verschieben"
                  onClick={() => navigate(routes.disposition.rescheduleTask(item.id, replanFrom))}
                />
              )}
              {canDelete && (
                <IconButton
                className="min-w-[40px]"
                  icon={<Trash2 size={16} />}
                  aria-label="Aufgabe löschen"
                  title="Aufgabe löschen"
                  onClick={askDelete}
                />
              )}
            </>
          }
        >
          <ListItem.Cell>{item.title}</ListItem.Cell>
          <ListItem.Cell>{item.assignedTechnician?.fullName ?? "—"}</ListItem.Cell>
          <ListItem.Cell>{getTaskTypeLabel(item.taskType)}</ListItem.Cell>
          <ListItem.Cell>
            {canPlanTask(item) ? (
              <button
                type="button"
                // The whole row opens the edit modal; this cell does something else.
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(routes.disposition.planTask(item.id));
                }}
                className="rounded border border-[#004494] px-2 py-1 text-xs font-semibold text-[#004494] transition hover:bg-[#004494] hover:text-white"
              >
                Planen
              </button>
            ) : (
              datum
            )}
          </ListItem.Cell>
          <ListItem.Cell>{abgabefrist}</ListItem.Cell>
          <ListItem.Cell>{soll}</ListItem.Cell>
          <ListItem.Cell>{ist}</ListItem.Cell>
          <ListItem.Cell className="!flex flex-row items-center justify-between gap-2">
            <div>{statusLabel}</div>
            {/* `shrink-0`: a long label must not squash the dot into an oval. */}
            <div
              className="h-6 w-6 shrink-0 rounded-full"
              style={{ backgroundColor: statusColor }}
            />
          </ListItem.Cell>
        </ListItem>
      </div>

      {/* Sibling of the row, not a child: inside that div every click in the dialog would bubble
          into `openEdit`. Mounted only while open — one hidden dialog per row would otherwise be
          left standing in a list that scrolls indefinitely. */}
      {deleteDialog.isOpen && (
        <DeleteItemDialog
          deleteModal={deleteDialog}
          singleLabel="Nur diese Aufgabe"
          seriesLabel="Diese und alle folgenden"
        />
      )}
    </>
  );
};
